import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { NotificationType, type OpenShiftRequest, type ShiftRequest } from "../../../utils/models";
import { useDeleteShift, useUpdateNursesNeeded } from "../hooks";
import { willFulfillRequest } from "./manualAddCompletionUtils";

type CompleteManualAddParams = {
	request: ShiftRequest;
	approvedNursesCount: number;
	source: string;
	suppressAddedSnackbar?: boolean;
};

/**
 * Shared follow-up for manually adding staff to an open shift request:
 * deletes the request once the last needed nurse is filled, otherwise decrements needed_nurses.
 * Used by both the web manual-add popup and the mobile add-nurse-to-shift screen.
 */
export const useManualAddCompletion = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { mutateAsync: deleteShift, isPending: isDeletingShift, isSuccess: isDeleteSuccess } = useDeleteShift();
	const { mutateAsync: updateNeededNurses, isPending: isUpdatingNeededNurses, isSuccess: isUpdateSuccess } = useUpdateNursesNeeded({});

	const completeManualAdd = useCallback(
		async ({ request, approvedNursesCount, source, suppressAddedSnackbar }: CompleteManualAddParams) => {
			const neededNurses = request.request_type === NotificationType.OpenShift ? (request as OpenShiftRequest).needed_nurses : 0;
			track("ManualStaffAddition_Completed", {
				source,
				requestId: request.id,
				neededNurses,
			});
			if (willFulfillRequest(approvedNursesCount, neededNurses)) {
				if (!suppressAddedSnackbar) showSnackbar(t("nurse.successfully.added.to.the.shift.removing.the.open.shift.request"), "info");
				track("ManualStaffAddition_RequestFulfilled_DeletingRequest", {
					source,
					requestId: request.id,
				});
				await deleteShift({ shiftId: request.id });
			} else {
				if (!suppressAddedSnackbar) showSnackbar(t("nurse.successfully.added.to.the.shift.decreasing.needed.nurses.from.request"), "info");
				const newNeededNurses = neededNurses - 1;
				track("ManualStaffAddition_UpdatingNeededNurses", {
					source,
					requestId: request.id,
					newNeededNurses,
				});
				await updateNeededNurses({ shiftId: request.id, neededNurses: newNeededNurses });
			}
		},
		[track, showSnackbar, t, deleteShift, updateNeededNurses],
	);

	return {
		completeManualAdd,
		isCompleting: isDeletingShift || isUpdatingNeededNurses,
		isCompletionSuccess: isDeleteSuccess || isUpdateSuccess,
	};
};
